import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { OutboxEntry, offlineDb } from './offline-db';
import { isApiRequest } from '../interceptors/auth';
import { currentSessionScope, SESSION_CHANGE_EVENT } from '../services/session-scope';

/**
 * Endpoints that keep working without a network.
 *
 * Deliberately a short list rather than the whole API: these are the screens a
 * clinic cannot stop using when the fibre drops or the power cuts — reception,
 * the queue, the consultation itself, vitals, and the pharmacy counter. Widening
 * it further would multiply the conflict cases without adding much.
 */
export const OFFLINE_PATHS = [
  '/patients',
  '/appointments',
  '/patient-queues',
  '/consultations',
  '/vital-records',
  '/pharmacy-sales',
  '/prescriptions',
];

/** Human label for the pending-changes list, derived from the request. */
function describe(method: string, url: string): string {
  const resource = OFFLINE_PATHS.find((p) => url.includes(p))?.slice(1) ?? 'donnée';
  const labels: Record<string, string> = {
    patients: 'patient',
    appointments: 'rendez-vous',
    'patient-queues': 'file d’attente',
    consultations: 'consultation',
    'vital-records': 'constantes',
    'pharmacy-sales': 'vente pharmacie',
    prescriptions: 'ordonnance',
  };
  const noun = labels[resource] ?? resource;
  if (method === 'POST') return `Création — ${noun}`;
  if (method === 'PUT' || method === 'PATCH') return `Modification — ${noun}`;
  if (method === 'DELETE') return `Suppression — ${noun}`;
  return noun;
}

@Injectable({ providedIn: 'root' })
export class OfflineService {
  private readonly http = inject(HttpClient);

  readonly isOnline = signal(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  readonly pending = signal<OutboxEntry[]>([]);
  readonly syncing = signal(false);
  readonly unassignedCount = signal(0);

  readonly pendingCount = computed(() => this.pending().length);
  readonly failedCount = computed(
    () => this.pending().filter((e) => !!e.error).length,
  );

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener(SESSION_CHANGE_EVENT, () => {
        this.pending.set([]);
        if (!currentSessionScope()) void offlineDb.clearCache().catch(() => undefined);
        void this.refreshPending();
      });
      window.addEventListener('online', () => {
        this.isOnline.set(true);
        void this.sync();
      });
      window.addEventListener('offline', () => this.isOnline.set(false));
    }
    void this.refreshPending();
  }

  /** True when this request is one the offline layer knows how to handle. */
  handles(url: string): boolean {
    return isApiRequest(url) && OFFLINE_PATHS.some((path) => {
      const pathname = new URL(url, window.location.origin).pathname;
      return pathname.endsWith(path) || pathname.includes(path + "/");
    });
  }

  cacheResponse(url: string, body: unknown, scope: string) {
    return offlineDb.putCached(url, body, scope).catch(() => undefined);
  }

  readCache(url: string, scope: string) {
    return offlineDb.getCached(url, scope).catch(() => undefined);
  }

  /** Parks a mutation until the network is back. */
  async queueMutation(method: string, url: string, body: unknown, scope: string) {
    if (!scope || scope !== currentSessionScope()) throw new Error("Session modifiée : reconnectez-vous avant de reprendre cette opération.");
    await offlineDb.enqueue({
      scope,
      method,
      url,
      body,
      label: describe(method, url),
      createdAt: Date.now(),
      attempts: 0,
    });
    await this.refreshPending();
  }

  async refreshPending() {
    try {
      const scope = currentSessionScope();
      const rows = scope ? await offlineDb.listOutbox(scope) : [];
      if (scope === currentSessionScope()) this.pending.set(rows);
      this.unassignedCount.set(await offlineDb.countUnassigned());
    } catch {
      this.pending.set([]);
    }
  }

  /**
   * Replays the outbox in the order the work was done — a consultation created
   * offline must exist before the prescription that references it.
   *
   * A 4xx means the server refused the change on its merits (a conflict, a
   * validation error). Retrying would never help, so the entry is kept and
   * flagged for a human instead of being silently dropped or retried forever.
   */
  async sync(): Promise<{ sent: number; failed: number }> {
    const scope = currentSessionScope();
    if (!scope || this.syncing() || !this.isOnline()) return { sent: 0, failed: 0 };
    this.syncing.set(true);

    let sent = 0;
    let failed = 0;
    try {
      const entries = (await offlineDb.listOutbox(scope))
        .filter((e) => !e.error)
        .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));

      for (const entry of entries) {
        if (scope !== currentSessionScope()) break;
        try {
          await firstValueFrom(
            this.http.request(entry.method, entry.url, {
              body: entry.body,
              // Bypass the offline interceptor: this *is* the replay.
              headers: { 'X-Offline-Replay': '1', 'X-Offline-Scope': scope },
            }),
          );
          if (entry.id !== undefined) await offlineDb.removeEntry(entry.id, scope);
          sent += 1;
        } catch (err: any) {
          const status = err?.status ?? 0;
          if (status >= 400 && status < 500) {
            await offlineDb.updateEntry({
              ...entry,
              attempts: entry.attempts + 1,
              error:
                err?.error?.message ??
                `Refusé par le serveur (${status}). À reprendre manuellement.`,
            });
            failed += 1;
          } else {
            // Network still flaky: stop and keep the queue intact and ordered.
            break;
          }
        }
      }
    } finally {
      this.syncing.set(false);
      await this.refreshPending();
    }

    return { sent, failed };
  }

  /** Drops an entry the user has decided to abandon or has redone by hand. */
  async discard(id: number) {
    const scope = currentSessionScope();
    if (!scope) return;
    await offlineDb.removeEntry(id, scope);
    await this.refreshPending();
  }
}
